import React, { useState } from 'react';
import { Button, CircularProgress, Box, Typography, IconButton, Tooltip } from '@mui/material';
import { followUser, unfollowUser } from '../../services/api';
import ConfirmDialog from '../dialogs/ConfirmDialog';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

interface FollowButtonProps {
  username: string;
  isFollowing: boolean;
  isOwnProfile: boolean;
  isFollowedBy?: boolean;
  onFollowChange?: (isFollowing: boolean) => void;
  /**
   * アイコンのみの省スペース表示。
   * 通常表示はボタン(minWidth 100px) + 「フォローされています」(約110px)で
   * 230px 前後を要求するため、狭い場所ではこちらを使う。
   * この表示では「フォローされています」の文言は出せない(幅が無いため)。
   * 代わりに向きを持つアイコン(←)で表し、意味はクッションの文言で補う。
   */
  iconOnly?: boolean;
  /** iconOnly のときにボタンへ当てる追加スタイル(バナー上のガラス風など) */
  iconSx?: object;
}

const FollowButton: React.FC<FollowButtonProps> = ({
  username,
  isFollowing,
  isOwnProfile,
  isFollowedBy = false,
  onFollowChange,
  iconOnly = false,
  iconSx
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isOwnProfile) {
    return null;
  }

  const applyFollowChange = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      if (isFollowing) {
        await unfollowUser(username);
        onFollowChange?.(false);
      } else {
        await followUser(username);
        onFollowChange?.(true);
      }
    } catch (error) {
      console.error('フォロー操作に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  // フォローもフォロー解除も、押した瞬間には実行しない。
  // 解除の誤爆だけでなく **フォローの誤爆も相手に通知が飛ぶ非可逆な操作** なので、
  // 両方にクッションを挟む。アイコンのみ表示では的が小さく誤タップが現実的なこと、
  // そして片思い状態の「←」アイコンの意味をここで言葉にして補えることも理由。
  const handleClick = () => setConfirmOpen(true);

  // 相互フォローは状態として独立しているので、専用のアイコンで表す。
  // (ドットバッジでは何を意味するか伝わらない)
  const isMutual = isFollowing && isFollowedBy;
  // 相手だけがフォローしている片思い状態。
  const isFollowedOnly = isFollowedBy && !isFollowing;

  const confirm = isFollowing
    ? {
        title: 'フォローを解除しますか？',
        message: isMutual
          ? `@${username} とは相互フォローです。解除してもよろしいですか？`
          : `@${username} のフォローを解除します。`,
        confirmText: '解除する',
      }
    : {
        title: isFollowedOnly ? 'フォローバックしますか？' : 'フォローしますか？',
        message: isFollowedOnly
          ? `@${username} はあなたをフォローしています。フォローを返すと相互フォローになります。`
          : `@${username} をフォローします。`,
        confirmText: 'フォローする',
      };

  const confirmDialog = (
    <ConfirmDialog
      open={confirmOpen}
      title={confirm.title}
      message={confirm.message}
      confirmText={confirm.confirmText}
      cancelText="キャンセル"
      onConfirm={applyFollowChange}
      onCancel={() => setConfirmOpen(false)}
    />
  );

  if (iconOnly) {
    // 片思い状態は「相手→あなた」という向きの情報なので、向きを持つ字形で表す。
    // 相互(⇄)と同じ字形を左右反転させた「←」にすることで、⇄ / ← が
    // 同じ記号体系として読める。意味の取り違えはクッションの文言が受け止める。
    const label = isMutual
      ? '相互フォロー'
      : isFollowing
        ? 'フォロー中'
        : isFollowedOnly
          ? 'フォローされています'
          : 'フォロー';
    const hint = isFollowing
      ? `${label}（タップでフォロー解除）`
      : isFollowedOnly
        ? `${label}（タップでフォローバック）`
        : label;
    return (
      <>
        <Tooltip title={hint}>
          <span>
            <IconButton size="small" onClick={handleClick} disabled={loading} aria-label={hint} sx={iconSx}>
              {loading
                ? <CircularProgress size={16} color="inherit" />
                : isMutual
                  ? <SwapHorizIcon fontSize="small" />
                  : isFollowing
                    ? <CheckIcon fontSize="small" />
                    : isFollowedOnly
                      ? <ArrowRightAltIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
                      : <PersonAddIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {confirmDialog}
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* **variant は渡さない。** かつて outlined/contained を切り替えていたが、
          直後の sx が両状態を同じ色で塗り潰していて1pxも効いていなかった。
          状態の差は下の sx が全部持つ。 */}
      <Button
        onClick={handleClick}
        disabled={loading}
        startIcon={loading ? undefined :
          isMutual
            ? <SwapHorizIcon sx={{ fontSize: '1rem' }} />
            : isFollowing
              ? <CheckIcon sx={{ fontSize: '1rem' }} />
              : <PersonAddIcon sx={{ fontSize: '1rem' }} />
        }
        sx={{
          minWidth: '104px',
          // 高さ・角丸はバナー上の他の操作に合わせる(アイコンボタン=円、
          // 画像操作ボタン=50px のピル)。8px の角丸はここだけ孤立していた。
          height: '28px',
          borderRadius: '50px',
          textTransform: 'none',
          fontSize: '0.72rem',
          fontWeight: 500,
          letterSpacing: '0.02em',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          // 素材は元のまま(半透明の白)。**アクセント色も黒ガラスも使わない。**
          // blur だけ足してある。明るいヘッダー画像を敷かれると白8%では地が透けて
          // 文字が沈むため、色感を変えずに可読性だけ上げる目的。
          color: '#fff',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          // 両状態を完全に同じ値にはしないこと(かつてそうなっていた)。押させたい的と
          // ただの状態表示が同じ重さになり、主導線が主導線に見えなくなる。
          // 色は変えず、濃度を一段だけ変えて段を付ける。
          ...(isFollowing
            ? {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.12)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
              }
            : {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.24)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderColor: 'rgba(255, 255, 255, 0.36)',
                },
              }),
          // ホバーで赤くしてラベルを「フォロー解除」に差し替える演出は廃止した。
          // 確認が無かった頃の作法で、クッションを入れた今は二重の警告になる。
          '&:disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            color: 'rgba(255, 255, 255, 0.3)',
          }
        }}
      >
        {loading ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          isMutual ? '相互フォロー' : isFollowing ? 'フォロー中' : 'フォロー'
        )}
      </Button>
      {/* 相互のときはボタン自体が「相互フォロー」と表示するので、この注記は不要。
          相手だけがフォローしている片思い状態のときだけ出す。 */}
      {isFollowedOnly && (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255, 255, 255, 0.75)',
            fontWeight: 400,
            fontSize: '0.6rem',
            lineHeight: '1.2',
            // ボタンと同じ素材。ヘッダー画像の上に載るので blur を持たせる。
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            padding: '3px 8px',
            borderRadius: '4px',
          }}
        >
          フォローされています
        </Typography>
      )}
      {confirmDialog}
    </Box>
  );
};

export default FollowButton; 